import React, { useState, useRef, useEffect } from 'react';

// Helper function to convert a simple markdown string to HTML
// This handles bold (**text**) and italic (*text*) and newlines.
const convertMarkdownToHtml = (markdown) => {
    if (!markdown) return '';
    let html = markdown;
    // Convert bold: **text** to <strong>text</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Convert italic: *text* to <em>text</em>
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Convert newlines to <br /> tags for proper line breaks in HTML
    html = html.replace(/\n/g, '<br />');
    return html;
};

// Main App component
const App = () => {
    // State to manage the current step of the application (upload/capture, user info, or summary)
    const [step, setStep] = useState('upload'); // 'upload', 'userInfo', or 'summary'
    // State to store the base64 encoded image data for the right hand
    const [rightHandImage, setRightHandImage] = useState(null);
    // State to store the base64 encoded image data for the left hand
    const [leftHandImage, setLeftHandImage] = useState(null);
    // State to store any error messages to display to the user
    const [errorMessage, setErrorMessage] = useState('');
    // State to store the palmistry summary generated by Gemini
    const [palmistrySummary, setPalmistrySummary] = useState('');
    // State to manage loading indicators during API calls
    const [isLoading, setIsLoading] = useState(false);
    // State to manage the visibility of the camera modal
    const [showCameraModal, setShowCameraModal] = useState(false);
    // State to determine which hand's image is being captured by the camera
    const [cameraTargetHand, setCameraTargetHand] = useState(null); // 'right' or 'left'
    // State for a temporary toast message (e.g., for "Copied to clipboard!")
    const [toastMessage, setToastMessage] = useState('');

    // States for user information
    const [userName, setUserName] = useState('');
    const [resultLanguage, setResultLanguage] = useState('English'); // Default to English

    // New state for chat modal visibility
    const [showChatModal, setShowChatModal] = useState(false);

    // Refs for video and canvas elements for camera capture
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    // Ref for the camera stream to stop it when not needed
    const streamRef = useRef(null);

    // Effect hook to clean up camera stream when component unmounts or modal closes
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Effect hook to hide toast message after a few seconds
    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => {
                setToastMessage('');
            }, 3000); // Hide after 3 seconds
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    // Function to handle image file selection
    const handleImageUpload = (event, hand) => {
        setErrorMessage(''); // Clear previous errors
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // Store the base64 image data
                if (hand === 'right') {
                    setRightHandImage(reader.result);
                } else {
                    setLeftHandImage(reader.result);
                }
            };
            reader.readAsDataURL(file); // Read file as Data URL
        }
    };

    // Function to open the camera modal and start camera stream
    const openCamera = async (hand) => {
        setErrorMessage('');
        setCameraTargetHand(hand);
        setShowCameraModal(true);
        try {
            // Attempt to get the rear camera first
            const constraints = {
                video: {
                    facingMode: { exact: "environment" } // Prioritize rear camera
                }
            };
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            } catch (envError) {
                console.warn("Environment camera not found or accessible, trying user-facing camera:", envError);
                // Fallback to front camera if environment camera fails
                stream = await navigator.mediaDevices.getUserMedia({ video: true });
            }

            streamRef.current = stream; // Store stream reference for cleanup
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (error) {
            console.error("Error accessing camera:", error);
            setErrorMessage("Could not access camera. Please ensure camera permissions are granted in your browser settings and try again.");
            setShowCameraModal(false); // Close modal on error
        }
    };

    // Function to capture an image from the camera stream
    const captureImage = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            // Set canvas dimensions to match video
            canvasRef.current.width = videoRef.current.videoWidth;
            canvasRef.current.height = videoRef.current.videoHeight;
            // Draw video frame onto canvas
            context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
            // Get image data as base64
            const imageData = canvasRef.current.toDataURL('image/png');

            if (cameraTargetHand === 'right') {
                setRightHandImage(imageData);
            } else {
                setLeftHandImage(imageData);
            }
            closeCameraModal(); // Close modal after capture
        }
    };

    // Function to close the camera modal and stop the camera stream
    const closeCameraModal = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop()); // Stop all tracks
        }
        setShowCameraModal(false);
        setCameraTargetHand(null);
    };

    // Function to extract base64 data from a Data URL (e.g., "data:image/png;base64,iVB2V...")
    const getBase64Data = (dataUrl) => {
        if (!dataUrl) return null;
        return dataUrl.split(',')[1]; // Split by comma and take the second part
    };

    // Helper function to handle API errors and return a user-friendly message
    const getApiErrorMessage = async (response) => {
        let errorDetail = "An unexpected error occurred.";
        try {
            const errorJson = await response.json();
            if (errorJson && errorJson.error && errorJson.error.message) {
                errorDetail = errorJson.error.message;
                if (errorDetail.includes("API key not valid")) {
                    return "API Key Error: Your Gemini API key might be invalid or missing. Please ensure it is correctly set in the code.";
                } else if (errorDetail.includes("blocked due to SAFETY")) {
                    return "Content blocked: The AI detected potentially unsafe content. Please ensure your images are clear and appropriate.";
                } else if (errorDetail.includes("Request payload size exceeds limit")) {
                    return "Image too large: The image file size might be too large. Please try uploading smaller images or capturing closer crops.";
                }
            } else if (response.status === 400) {
                return `Bad Request (400): The server received an invalid request. This might mean the images are not recognized as palms, or the prompt was malformed.`;
            } else if (response.status === 429) {
                return `Too Many Requests (429): You've sent too many requests to the API too quickly. Please wait a moment and try again.`;
            } else if (response.status >= 500) {
                return `Server Error (${response.status}): The AI server is experiencing issues. Please try again in a few minutes.`;
            }
        } catch (parseError) {
            console.error("Error parsing API error response:", parseError);
        }
        return `API Error (${response.status || 'unknown'}): ${errorDetail}. Please check your internet connection and try again.`;
    };


    // Function to validate images using Gemini API
    const validateImages = async () => {
        setErrorMessage(''); // Clear previous errors
        if (!rightHandImage || !leftHandImage) {
            setErrorMessage("Please upload or capture both right and left hand images.");
            return;
        }

        setIsLoading(true); // Show loading indicator

        try {
            const prompt = `Analyze these two images.
            Image 1 is intended to be the right hand. Please confirm if it is a human palm and if the thumb is oriented towards the right side of the image.
            Image 2 is intended to be the left hand. Please confirm if it is a human palm and if the thumb is oriented towards the left side of the image.
            Respond with 'VALID' if both conditions are met for both images. Otherwise, respond with 'INVALID' and briefly explain why (e.g., 'Not a palm', 'Right hand thumb not to the right', 'Left hand thumb not to the left').`;

            // Prepare payload for Gemini API
            const payload = {
                contents: [
                    {
                        role: "user",
                        parts: [
                            { text: prompt },
                            {
                                inlineData: {
                                    mimeType: "image/png",
                                    data: getBase64Data(rightHandImage) // Right hand image
                                }
                            },
                            {
                                inlineData: {
                                    mimeType: "image/png",
                                    data: getBase64Data(leftHandImage) // Left hand image
                                }
                            }
                        ]
                    }
                ],
            };

            const apiKey = ""; // IMPORTANT: In a real app, replace with your actual API key here or use environment variables.
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            // Make API call to Gemini
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const detailedError = await getApiErrorMessage(response);
                setErrorMessage(`Image validation failed: ${detailedError}`);
                return;
            }

            const result = await response.json();

            // Check if response structure is valid and contains content
            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const text = result.candidates[0].content.parts[0].text.trim().toUpperCase(); // Convert to uppercase for consistent checking

                if (text.startsWith('VALID')) {
                    // If validation is successful, proceed to user info page
                    setStep('userInfo');
                } else if (text.startsWith('INVALID')) {
                    // If validation fails, display the error message from Gemini
                    setErrorMessage(`Validation failed: ${text.replace('INVALID:', '').trim()}. Please ensure both images are clearly visible palms with correct thumb orientation.`);
                } else {
                    // Handle unexpected responses from Gemini
                    setErrorMessage("Image validation failed due to an unexpected AI response. Please ensure images are clear and try again.");
                }
            } else {
                setErrorMessage("Failed to get a valid response for image validation. The AI might not have understood the images. Please try again with clearer photos.");
            }
        } catch (error) {
            console.error("Error during image validation:", error);
            setErrorMessage(`An error occurred during image validation: ${error.message}. Please check your internet connection and ensure your API key is correct.`);
        } finally {
            setIsLoading(false); // Hide loading indicator
        }
    };

    // Function to generate palmistry summary using Gemini API
    const generatePalmistrySummary = async () => {
        setErrorMessage(''); // Clear any previous errors
        if (!userName || !resultLanguage) {
            setErrorMessage("Please fill in your name and select a language to proceed.");
            return;
        }

        setIsLoading(true); // Show loading indicator

        try {
            const prompt = `Based on these two images of human palms (Image 1: Right Hand, Image 2: Left Hand), provide a general palmistry summary.
            Customize the summary for a person named ${userName}.
            Focus on major lines (heart, head, life) and mounts, and offer insights into personality traits, potential life paths, and general fortune.
            The response should be in ${resultLanguage}. Do not make any medical or definitive predictions. Keep the summary concise and positive, around 200-300 words.
            Use markdown for formatting, specifically **bold** and *italic* where appropriate.`;

            const payload = {
                contents: [
                    {
                        role: "user",
                        parts: [
                            { text: prompt },
                            {
                                inlineData: {
                                    mimeType: "image/png",
                                    data: getBase64Data(rightHandImage)
                                }
                            },
                            {
                                inlineData: {
                                    mimeType: "image/png",
                                    data: getBase64Data(leftHandImage)
                                }
                            }
                        ]
                    }
                ],
            };

            const apiKey = ""; // IMPORTANT: In a real app, replace with your actual API key here or use environment variables.
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const detailedError = await getApiErrorMessage(response);
                setErrorMessage(`Failed to generate palmistry summary: ${detailedError}`);
                return;
            }

            const result = await response.json();

            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const summary = result.candidates[0].content.parts[0].text;
                setPalmistrySummary(summary);
                setStep('summary'); // Transition to summary page
            } else {
                setErrorMessage("Failed to generate palmistry summary. The AI could not produce a reading. Please try again with different or clearer images.");
            }
        } catch (error) {
            console.error("Error generating palmistry summary:", error);
            setErrorMessage(`An error occurred while generating the summary: ${error.message}. Please check your internet connection and ensure your API key is correct.`);
        } finally {
            setIsLoading(false); // Hide loading indicator
        }
    };

    // Function to handle sharing the summary
    const handleShare = async () => {
        const shareUrl = "https://palmistry.bykapil.com"; // Your deployed app URL
        const shareText = `Hey, I just used this AI palm reading app and here's what it says about me: '${palmistrySummary}' Try it yourself here - ${shareUrl}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'My Palm Reading',
                    text: shareText,
                    url: shareUrl,
                });
                setToastMessage('Shared successfully!');
            } catch (error) {
                if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
                    console.warn('Web Share API failed, falling back to clipboard:', error);
                    try {
                        await navigator.clipboard.writeText(shareText);
                        setToastMessage('Sharing not supported or cancelled. Palm reading copied to clipboard!');
                    } catch (clipboardErr) {
                        console.error('Failed to copy text to clipboard:', clipboardErr);
                        setToastMessage('Failed to copy to clipboard.');
                    }
                } else {
                    console.error('Error sharing:', error);
                    setToastMessage('An error occurred during sharing. Please try again.');
                }
            }
        } else {
            try {
                await navigator.clipboard.writeText(shareText);
                setToastMessage('Sharing not supported. Palm reading copied to clipboard!');
            } catch (err) {
                console.error('Failed to copy text to clipboard:', err);
                setToastMessage('Failed to copy to clipboard.');
            }
        }
    };

    // Function to handle the "Want to know more? Let's chat" button click
    const handleChatClick = () => {
        setShowChatModal(true);
    };


    // Render the upload/capture page
    const renderUploadPage = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-purple-100 to-indigo-200 p-4 font-sans">
            <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">AI Palm Reader</h1>
            <h3 className="text-1xl font-bold text-gray-800 mb-2 text-center">Designed by Kapil</h3>
            <p className="text-gray-600 text-sm mb-8 text-center">All data, including palm images and personal information, is processed solely within your browser and is never stored on our servers.</p> {/* Privacy Disclaimer */}

            {/* Changed order for Left Hand on Left, Right Hand on Right */}
            <div className="flex flex-col md:flex-row gap-8 mb-8 w-full max-w-4xl">
                {/* Left Hand Upload Box (now first) */}
                <div className="flex-1 bg-white p-6 rounded-xl shadow-lg flex flex-col items-center border-2 border-purple-300">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">Left Hand</h2>
                    <div className="w-64 h-64 border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center overflow-hidden mb-4 bg-gray-50">
                        {leftHandImage ? (
                            <img src={leftHandImage} alt="Left Hand" className="object-cover w-full h-full" />
                        ) : (
                            <span className="text-gray-500">Upload or Capture</span>
                        )}
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'left')}
                        className="hidden"
                        id="leftHandUpload"
                    />
                    <label
                        htmlFor="leftHandUpload"
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg cursor-pointer transition duration-300 ease-in-out shadow-md mb-2 w-full text-center"
                    >
                        Upload Left Hand
                    </label>
                    <button
                        onClick={() => openCamera('left')}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out shadow-md w-full text-center"
                    >
                        Capture Left Hand
                    </button>
                </div>

                {/* Right Hand Upload Box (now second) */}
                <div className="flex-1 bg-white p-6 rounded-xl shadow-lg flex flex-col items-center border-2 border-purple-300">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">Right Hand</h2>
                    <div className="w-64 h-64 border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center overflow-hidden mb-4 bg-gray-50">
                        {rightHandImage ? (
                            <img src={rightHandImage} alt="Right Hand" className="object-cover w-full h-full" />
                        ) : (
                            <span className="text-gray-500">Upload or Capture</span>
                        )}
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'right')}
                        className="hidden"
                        id="rightHandUpload"
                    />
                    <label
                        htmlFor="rightHandUpload"
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg cursor-pointer transition duration-300 ease-in-out shadow-md mb-2 w-full text-center"
                    >
                        Upload Right Hand
                    </label>
                    <button
                        onClick={() => openCamera('right')}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out shadow-md w-full text-center"
                    >
                        Capture Right Hand
                    </button>
                </div>
            </div>

            {errorMessage && (
                <p className="text-red-600 bg-red-100 p-3 rounded-lg mb-6 w-full max-w-md text-center shadow-sm">
                    {errorMessage}
                </p>
            )}

            <button
                onClick={validateImages}
                disabled={isLoading || !rightHandImage || !leftHandImage}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-xl transition duration-300 ease-in-out shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
                {isLoading ? (
                    <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Analyzing...
                    </>
                ) : (
                    'Analyze Palms'
                )}
            </button>

            {/* Camera Modal */}
            {showCameraModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-6 rounded-xl shadow-2xl flex flex-col items-center max-w-xl w-full">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">Capture {cameraTargetHand === 'right' ? 'Right' : 'Left'} Hand</h2>
                        <video ref={videoRef} className="w-full h-auto rounded-lg mb-4 border-2 border-gray-300" autoPlay playsInline></video>
                        <canvas ref={canvasRef} className="hidden"></canvas> {/* Hidden canvas for image processing */}
                        <div className="flex gap-4 w-full justify-center">
                            <button
                                onClick={captureImage}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out shadow-md flex-1"
                            >
                                Take Photo
                            </button>
                            <button
                                onClick={closeCameraModal}
                                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out shadow-md flex-1"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Render function for user information
    const renderUserInfoPage = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-purple-100 to-indigo-200 p-4 font-sans">
            <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">Tell Us About Yourself</h1>

            <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mb-8 border-2 border-purple-300">
                <div className="mb-6">
                    <label htmlFor="userName" className="block text-gray-700 text-lg font-semibold mb-2">Your Name:</label>
                    <input
                        type="text"
                        id="userName"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800"
                        placeholder="e.g., Alex"
                    />
                </div>
                <div className="mb-8">
                    <label htmlFor="resultLanguage" className="block text-gray-700 text-lg font-semibold mb-2">Result Language:</label>
                    <select
                        id="resultLanguage"
                        value={resultLanguage}
                        onChange={(e) => setResultLanguage(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800 bg-white"
                    >
                        <option value="English">English</option>
                        <option value="Hindi">Hindi</option>
                    </select>
                </div>

                {errorMessage && (
                    <p className="text-red-600 bg-red-100 p-3 rounded-lg mb-6 w-full text-center shadow-sm">
                        {errorMessage}
                    </p>
                )}

                <button
                    onClick={generatePalmistrySummary}
                    disabled={isLoading || !userName || !resultLanguage}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-xl transition duration-300 ease-in-out shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-full"
                >
                    {isLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                            Generating Summary...
                        </>
                    ) : (
                        'Get My Palmistry Summary'
                    )}
                </button>
            </div>
        </div>
    );


    // Render the palmistry summary page
    const renderSummaryPage = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-purple-100 to-indigo-200 p-4 font-sans">
            <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">Palmistry Summary</h1>

            <div className="bg-white p-8 rounded-xl shadow-lg max-w-3xl w-full mb-8 border-2 border-purple-300">
                {isLoading ? (
                    <div className="text-center text-gray-600 text-lg">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                        Generating your personalized palmistry summary...
                    </div>
                ) : (
                    <>
                        {/* Use dangerouslySetInnerHTML to render HTML from markdown conversion */}
                        <div className="text-gray-700 text-lg leading-relaxed summary-content" dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(palmistrySummary) }}></div>
                        {errorMessage && (
                            <p className="text-red-600 bg-red-100 p-3 rounded-lg mt-6 text-center shadow-sm">
                                {errorMessage}
                            </p>
                        )}
                    </>
                )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full max-w-lg"> {/* Increased max-w for 3 buttons */}
                {/* New 'Want to know more? Let's chat' button (first) */}
                <button
                    onClick={handleChatClick}
                    className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-6 rounded-lg text-lg transition duration-300 ease-in-out shadow-md flex-1"
                >
                    Want to know more? Let's chat
                </button>
                {/* Share Reading button (second) */}
                <button
                    onClick={handleShare}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg text-lg transition duration-300 ease-in-out shadow-md flex-1 flex items-center justify-center"
                >
                    {/* SVG for share icon */}
                    <svg width="24px" height="24px" strokeWidth="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" style={{ stroke: 'currentColor' }}>
                        <path d="M18 22C19.6569 22 21 20.6569 21 19C21 17.3431 19.6569 16 18 16C16.3431 16 15 17.3431 15 19C15 20.6569 16.3431 22 18 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 6.65685 16.3431 8 18 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M6 15C7.65685 15 9 13.6569 9 12C9 10.3431 7.65685 9 6 9C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M15.5 6.5L8.5 10.5" stroke="currentColor" strokeWidth="1.5"></path>
                        <path d="M8.5 13.5L15.5 17.5" stroke="currentColor" strokeWidth="1.5"></path>
                    </svg>
                    Share Reading
                </button>
                {/* Analyze New Palms button (last) */}
                <button
                    onClick={() => {
                        setStep('upload'); // Go back to upload page
                        setPalmistrySummary(''); // Clear summary
                        setRightHandImage(null); // Clear images
                        setLeftHandImage(null);
                        setErrorMessage(''); // Clear errors
                        setUserName(''); // Clear user info
                        setResultLanguage('English');
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg text-lg transition duration-300 ease-in-out shadow-md flex-1"
                >
                    Analyze New Palms
                </button>
            </div>

            {toastMessage && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm z-50">
                    {toastMessage}
                </div>
            )}

            {/* Chat Modal */}
            {showChatModal && (
                <ChatModal
                    onClose={() => setShowChatModal(false)}
                    userName={userName}
                    palmistrySummary={palmistrySummary}
                    rightHandImage={rightHandImage}
                    leftHandImage={leftHandImage}
                    resultLanguage={resultLanguage} // Pass language to ChatModal
                />
            )}
        </div>
    );

    // Conditional rendering based on the current step
    return (
        <div className="App">
            {step === 'upload' && renderUploadPage()}
            {step === 'userInfo' && renderUserInfoPage()}
            {step === 'summary' && renderSummaryPage()}
        </div>
    );
};

// Chat Modal Component
const ChatModal = ({ onClose, userName, palmistrySummary, rightHandImage, leftHandImage, resultLanguage }) => {
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Initial message from the palm reader
    useEffect(() => {
        const welcomeMessageEnglish = `Hello ${userName}! I am your AI Palm Reader. Feel free to ask me anything related to relationships, career, or life based on your palm insights. How can I assist you today?`;
        const welcomeMessageHindi = `नमस्ते ${userName}! मैं आपकी एआई हस्तरेखा विशेषज्ञ हूँ। अपनी हथेली की अंतर्दृष्टि के आधार पर रिश्तों, करियर या जीवन से संबंधित कुछ भी पूछने के लिए स्वतंत्र महसूस करें। मैं आज आपकी कैसे सहायता कर सकती हूँ?`;

        setChatMessages([
            { role: 'model', text: resultLanguage === 'Hindi' ? welcomeMessageHindi : welcomeMessageEnglish }
        ]);
    }, [userName, resultLanguage]); // Added resultLanguage to dependency array

    // Scroll to the latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatMessages]);

    // Function to extract base64 data from a Data URL (e.g., "data:image/png;base64,iVB2V...")
    const getBase64Data = (dataUrl) => {
        if (!dataUrl) return null;
        return dataUrl.split(',')[1]; // Split by comma and take the second part
    };

    const handleSendMessage = async () => {
        if (chatInput.trim() === '') return;

        const userMessage = chatInput;
        setChatInput(''); // Clear input immediately
        setChatMessages((prevMessages) => [...prevMessages, { role: 'user', text: userMessage }]);
        setIsChatLoading(true);

        try {
            // Base prompt structure
            let prompt = `You are an AI Palm Reader. The user's name is ${userName}. Their previous palmistry summary was: "${palmistrySummary}".
            The user is asking: "${userMessage}".
            Based on the provided palm images (Image 1: Right Hand, Image 2: Left Hand) and the summary, answer the user's question.
            Focus on relationships, career, or life aspects as a palm reader would.
            Keep the response concise and in ${resultLanguage}. Do not make medical or definitive predictions.`;

            // Adjust prompt for Hindi if selected
            if (resultLanguage === 'Hindi') {
                prompt = `आप एक एआई हस्तरेखा विशेषज्ञ हैं। उपयोगकर्ता का नाम ${userName} है। उनकी पिछली हस्तरेखा सारांश था: "${palmistrySummary}"।
                उपयोगकर्ता पूछ रहा है: "${userMessage}"।
                प्रदान की गई हथेली की छवियों (छवि 1: दाहिना हाथ, छवि 2: बायां हाथ) और सारांश के आधार पर उपयोगकर्ता के प्रश्न का उत्तर दें।
                एक हस्तरेखा विशेषज्ञ के रूप में रिश्तों, करियर या जीवन के पहलुओं पर ध्यान दें।
                जवाब संक्षिप्त और हिंदी में होना चाहिए। चिकित्सा या निश्चित भविष्यवाणियां न करें।`;
            }


            let contents = [
                { role: "user", parts: [{ text: prompt }] }
            ];

            // Conditionally add images if available
            if (rightHandImage && leftHandImage) {
                contents[0].parts.push({
                    inlineData: {
                        mimeType: "image/png",
                        data: getBase64Data(rightHandImage)
                    }
                });
                contents[0].parts.push({
                    inlineData: {
                        mimeType: "image/png",
                        data: getBase64Data(leftHandImage)
                    }
                });
            } else {
                // If images are not available, adjust prompt or inform user
                // Adjust prompt for Hindi if selected and images are not available
                if (resultLanguage === 'Hindi') {
                    contents[0].parts[0].text = `आप एक एआई हस्तरेखा विशेषज्ञ हैं। उपयोगकर्ता का नाम ${userName} है। उनकी पिछली हस्तरेखा सारांश था: "${palmistrySummary}"।
                    ध्यान दें: इस चैट संदर्भ के लिए हथेली की छवियां उपलब्ध नहीं हैं। कृपया प्रदान किए गए सारांश और सामान्य हस्तरेखा ज्ञान के आधार पर उपयोगकर्ता के प्रश्न का उत्तर दें।
                    उपयोगकर्ता पूछ रहा है: "${userMessage}"।
                    एक हस्तरेखा विशेषज्ञ के रूप में रिश्तों, करियर या जीवन के पहलुओं पर ध्यान दें।
                    जवाब संक्षिप्त और हिंदी में होना चाहिए। चिकित्सा या निश्चित भविष्यवाणियां न करें।`;
                } else {
                    contents[0].parts[0].text = `You are an AI Palm Reader. The user's name is ${userName}. Their previous palmistry summary was: "${palmistrySummary}".
                    Note: Palm images are not available for this chat context. Please answer the user's question based on the provided summary and general palmistry knowledge.
                    The user is asking: "${userMessage}".
                    Focus on relationships, career, or life aspects as a palm reader would.
                    Keep the response concise and in ${resultLanguage}. Do not make medical or definitive predictions.`;
                }
            }

            const payload = { contents: contents };

            const apiKey = "";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData.error?.message || `API Error: ${response.status}`;
                setChatMessages((prevMessages) => [...prevMessages, { role: 'model', text: `Error: ${errorMessage}. Please try again.` }]);
                console.error("Gemini API error:", errorMessage);
                return;
            }

            const result = await response.json();
            if (result.candidates && result.candidates.length > 0 &&
                result.candidates[0].content && result.candidates[0].content.parts &&
                result.candidates[0].content.parts.length > 0) {
                const modelResponse = result.candidates[0].content.parts[0].text;
                setChatMessages((prevMessages) => [...prevMessages, { role: 'model', text: modelResponse }]);
            } else {
                setChatMessages((prevMessages) => [...prevMessages, { role: 'model', text: "I couldn't generate a response. Please try rephrasing your question." }]);
            }
        } catch (error) {
            console.error("Error during chat API call:", error);
            setChatMessages((prevMessages) => [...prevMessages, { role: 'model', text: "An error occurred while connecting to the AI. Please check your internet and try again." }]);
        } finally {
            setIsChatLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl flex flex-col w-full max-w-xl h-[80vh]">
                <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200"> {/* Adjusted mb-2 */}
                    <h2 className="text-2xl font-bold text-gray-800">Chat with AI Palm Reader</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
                    >
                        &times;
                    </button>
                </div>
                {/* Privacy Disclaimer for Chat */}
                <p className="text-gray-600 text-xs mb-4 text-center">Your chat messages are processed in real-time by the AI and are not stored or uploaded to ensure your privacy.</p>

                <div className="flex-1 overflow-y-auto pr-2 mb-4 space-y-4">
                    {chatMessages.map((msg, index) => (
                        <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] p-3 rounded-lg shadow-sm ${
                                msg.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'
                            }`}>
                                {msg.text}
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} /> {/* Scroll target */}
                </div>
                <div className="flex">
                    <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !isChatLoading) handleSendMessage(); }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800"
                        placeholder="Ask me anything..."
                        disabled={isChatLoading}
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={isChatLoading || chatInput.trim() === ''}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-r-lg transition duration-300 ease-in-out shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isChatLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            'Send'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default App;
